import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import * as Sentry from '@sentry/browser';
import { formatCurrency } from './calculations';

/**
 * Export amortization schedule as PDF
 * @param {Object} data - Calculation results and schedule
 * @param {string} calculatorType - Type of calculator used
 */
export function exportToPdf(data, calculatorType = 'standard') {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(18);
    doc.text('Loan Repayment Schedule', pageWidth / 2, 15, { align: 'center' });
    
    // Calculator type
    doc.setFontSize(12);
    doc.text(`Calculator: ${calculatorType.charAt(0).toUpperCase() + calculatorType.slice(1)}`, pageWidth / 2, 22, { align: 'center' });
    
    // Current date
    const currentDate = new Date().toLocaleDateString();
    doc.setFontSize(10);
    doc.text(`Generated on: ${currentDate}`, pageWidth / 2, 28, { align: 'center' });
    
    // Summary section
    doc.setFontSize(14);
    doc.text('Summary', 14, 40);
    
    doc.setFontSize(10);
    let summaryData = [];
    
    if (calculatorType === 'early') {
      // Early repayment specific summary
      summaryData = [
        ['Current Balance', formatCurrency(data.currentBalance)],
        ['Interest Rate', `${data.annualInterestRate}%`],
        ['Remaining Term', `${data.remainingMonths} months`],
        ['Current Monthly Payment', formatCurrency(data.currentMonthlyPayment)],
        ['Full Settlement Amount', formatCurrency(data.fullSettlementAmount)],
        ['Interest Savings (Full Settlement)', formatCurrency(data.interestSavingsFromFullSettlement)]
      ];
      
      if (data.earlyPaymentAmount > 0) {
        summaryData.push(['Early Payment Amount', formatCurrency(data.earlyPaymentAmount)]);
        
        if (data.reducePaymentScenario) {
          summaryData.push(
            ['New Monthly Payment (Reduced)', formatCurrency(data.reducePaymentScenario.newMonthlyPayment)],
            ['Monthly Savings', formatCurrency(data.reducePaymentScenario.monthlySavings)]
          );
        }
        
        if (data.reduceTermScenario && !data.reduceTermScenario.error) {
          summaryData.push(
            ['New Term (Months)', data.reduceTermScenario.newTermMonths.toString()],
            ['Months Saved', data.reduceTermScenario.monthsSaved.toString()]
          );
        }
      }
    } else {
      // Standard summary for other calculators
      summaryData = [
        ['Principal Amount', formatCurrency(data.principal)],
        ['Interest Rate', `${data.annualInterestRate}%`],
        ['Loan Term', `${data.termInMonths} months (${(data.termInMonths / 12).toFixed(1)} years)`],
        ['Monthly Payment', formatCurrency(data.monthlyPayment)],
        ['Total Interest', formatCurrency(data.totalInterest)],
        ['Total Repayment', formatCurrency(data.totalRepaid)]
      ];
      
      // Add calculator-specific data
      if (calculatorType === 'missed') {
        summaryData.push(
          ['Missed Payments', data.missedPayments.join(', ')],
          ['Late Fee per Payment', formatCurrency(data.lateFee)],
          ['Total Late Fees', formatCurrency(data.totalLateFees)],
          ['Additional Months Required', data.additionalMonths.toString()]
        );
      } else if (calculatorType === 'restructure') {
        if (data.restructureType === 'spread') {
          summaryData.push(
            ['Arrears Amount', formatCurrency(data.arrearsAmount)],
            ['New Monthly Payment', formatCurrency(data.newMonthlyPayment)],
            ['Payment Increase', formatCurrency(data.increasedMonthlyAmount)],
            ['Percentage Increase', `${data.percentageIncrease.toFixed(2)}%`]
          );
        } else if (data.restructureType === 'extend') {
          summaryData.push(
            ['Arrears Amount', formatCurrency(data.arrearsAmount)],
            ['Original Term', `${data.originalTermRemaining} months`],
            ['Extended Term', `${data.newTermMonths} months`],
            ['Additional Months', data.additionalMonths.toString()]
          );
        } else if (data.restructureType === 'reduce') {
          summaryData.push(
            ['Arrears Amount', formatCurrency(data.arrearsAmount)],
            ['Original Monthly Payment', formatCurrency(data.originalMonthlyPayment)],
            ['Reduced Monthly Payment', formatCurrency(data.newMonthlyPayment)],
            ['New Term', `${data.newTermMonths} months`]
          );
        }
      }
    }
    
    // Draw summary table
    autoTable(doc, {
      startY: 45,
      head: [['Item', 'Value']],
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: [66, 139, 202] }
    });

    // Get schedule for early repayment
    let scheduleToExport = data.schedule;
    if (calculatorType === 'early') {
      if (data.reducePaymentScenario && data.reducePaymentScenario.schedule) {
        scheduleToExport = data.reducePaymentScenario.schedule;
      } else if (data.reduceTermScenario && data.reduceTermScenario.schedule) {
        scheduleToExport = data.reduceTermScenario.schedule;
      } else {
        scheduleToExport = [];
      }
    }
    
    // Schedule section
    if (scheduleToExport && scheduleToExport.length > 0) {
      doc.setFontSize(14);
      doc.text('Amortization Schedule', 14, doc.lastAutoTable.finalY + 15);
      
      // Convert amortization schedule for the table
      const scheduleData = scheduleToExport.map(payment => {
        let row = [
          payment.month.toString(),
          formatCurrency(payment.payment),
          formatCurrency(payment.principal),
          formatCurrency(payment.interest),
          formatCurrency(payment.balance),
          formatCurrency(payment.remainingToRepay)
        ];
        
        // Add missed payment indicator for missed payment calculator
        if (calculatorType === 'missed') {
          row.push(payment.missed ? 'Yes' : 'No');
          if (payment.lateFee && payment.lateFee > 0) {
            row.push(formatCurrency(payment.lateFee));
          } else {
            row.push('-');
          }
        }
        
        // Add interest frozen indicator for interest accrual calculator
        if (calculatorType === 'interest' && payment.interestFrozen) {
          row.push('Yes');
          row.push(formatCurrency(payment.originalInterest || 0));
        } else if (calculatorType === 'interest') {
          row.push('No');
          row.push('-');
        }
        
        return row;
      });
      
      // Define columns based on calculator type
      let columns = ['Month', 'Payment', 'Principal', 'Interest', 'Balance', 'Remaining to Repay'];
      if (calculatorType === 'missed') {
        columns.push('Missed', 'Late Fee');
      } else if (calculatorType === 'interest') {
        columns.push('Interest Frozen', 'Original Interest');
      }
      
      // Draw schedule table
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 20,
        head: [columns],
        body: scheduleData,
        theme: 'grid',
        headStyles: { fillColor: [66, 139, 202] },
        columnStyles: {
          0: { cellWidth: 15 } // Month column width
        },
        didDrawPage: function (data) {
          // Add page number at the bottom
          const pageNumber = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.text(
            `Page ${pageNumber}`,
            pageWidth / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: 'center' }
          );
        }
      });
    }
    
    // Add disclaimer
    doc.setFontSize(8);
    doc.text(
      'This calculation is for informational purposes only and does not constitute financial advice.',
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 20,
      { align: 'center' }
    );
    
    // Save the PDF
    doc.save(`loan-schedule-${calculatorType}-${new Date().toISOString().split('T')[0]}.pdf`);
    
    console.log('PDF export completed successfully');
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    Sentry.captureException(error);
    alert('An error occurred while exporting to PDF. Please try again.');
  }
}

/**
 * Export amortization schedule as Excel
 * @param {Object} data - Calculation results and schedule
 * @param {string} calculatorType - Type of calculator used
 */
export function exportToExcel(data, calculatorType = 'standard') {
  try {
    // Create workbook
    const workbook = new Workbook();
    
    // Add summary sheet
    const summarySheet = workbook.addWorksheet('Summary');
    
    // Summary data
    let summaryRows = [
      ['Loan Summary'],
      ['']
    ];
    
    if (calculatorType === 'early') {
      summaryRows.push(
        ['Current Balance', data.currentBalance.toString()],
        ['Interest Rate (%)', data.annualInterestRate.toString()],
        ['Remaining Term (months)', data.remainingMonths.toString()],
        ['Current Monthly Payment', data.currentMonthlyPayment.toString()],
        ['Full Settlement Amount', data.fullSettlementAmount.toString()],
        ['Interest Savings (Full Settlement)', data.interestSavingsFromFullSettlement.toString()]
      );
      
      if (data.earlyPaymentAmount > 0) {
        summaryRows.push(
          [''],
          ['Early Payment Amount', data.earlyPaymentAmount.toString()]
        );
        
        if (data.reducePaymentScenario) {
          summaryRows.push(
            ['New Monthly Payment (Reduced)', data.reducePaymentScenario.newMonthlyPayment.toString()],
            ['Monthly Savings', data.reducePaymentScenario.monthlySavings.toString()]
          );
        }
        
        if (data.reduceTermScenario && !data.reduceTermScenario.error) {
          summaryRows.push(
            ['New Term (Months)', data.reduceTermScenario.newTermMonths.toString()],
            ['Months Saved', data.reduceTermScenario.monthsSaved.toString()]
          );
        }
      }
    } else {
      summaryRows.push(
        ['Principal Amount', data.principal.toString()],
        ['Interest Rate (%)', data.annualInterestRate.toString()],
        ['Loan Term (months)', data.termInMonths.toString()],
        ['Monthly Payment', data.monthlyPayment.toString()],
        ['Total Interest', data.totalInterest.toString()],
        ['Total Repayment', data.totalRepaid.toString()]
      );
      
      // Add calculator-specific data
      if (calculatorType === 'missed') {
        summaryRows.push(
          [''],
          ['Missed Payments', data.missedPayments.join(', ')],
          ['Late Fee per Payment', data.lateFee.toString()],
          ['Total Late Fees', data.totalLateFees.toString()],
          ['Additional Months Required', data.additionalMonths.toString()]
        );
      } else if (calculatorType === 'restructure') {
        summaryRows.push(['']);
        if (data.restructureType === 'spread') {
          summaryRows.push(
            ['Restructure Type', 'Spread Arrears'],
            ['Arrears Amount', data.arrearsAmount.toString()],
            ['New Monthly Payment', data.newMonthlyPayment.toString()],
            ['Payment Increase', data.increasedMonthlyAmount.toString()],
            ['Percentage Increase (%)', data.percentageIncrease.toString()]
          );
        } else if (data.restructureType === 'extend') {
          summaryRows.push(
            ['Restructure Type', 'Extend Term'],
            ['Arrears Amount', data.arrearsAmount.toString()],
            ['Original Term (months)', data.originalTermRemaining.toString()],
            ['Extended Term (months)', data.newTermMonths.toString()],
            ['Additional Months', data.additionalMonths.toString()]
          );
        } else if (data.restructureType === 'reduce') {
          summaryRows.push(
            ['Restructure Type', 'Reduce Payment'],
            ['Arrears Amount', data.arrearsAmount.toString()],
            ['Original Monthly Payment', data.originalMonthlyPayment.toString()],
            ['Reduced Monthly Payment', data.newMonthlyPayment.toString()],
            ['New Term (months)', data.newTermMonths.toString()]
          );
        }
      } else if (calculatorType === 'interest') {
        if (data.freezeStartMonth && data.freezeEndMonth) {
          summaryRows.push(
            [''],
            ['Interest Freeze Start Month', data.freezeStartMonth.toString()],
            ['Interest Freeze End Month', data.freezeEndMonth.toString()]
          );
        }
      }
    }
    
    // Add summary rows to worksheet
    summaryRows.forEach(row => {
      summarySheet.addRow(row);
    });

    // Get schedule for early repayment
    let scheduleToExport = data.schedule;
    if (calculatorType === 'early') {
      if (data.reducePaymentScenario && data.reducePaymentScenario.schedule) {
        scheduleToExport = data.reducePaymentScenario.schedule;
      } else if (data.reduceTermScenario && data.reduceTermScenario.schedule) {
        scheduleToExport = data.reduceTermScenario.schedule;
      } else {
        scheduleToExport = [];
      }
    }
    
    // Schedule sheet
    if (scheduleToExport && scheduleToExport.length > 0) {
      const scheduleSheet = workbook.addWorksheet('Amortization Schedule');
      
      // Create header row based on calculator type
      let headers = ['Month', 'Payment', 'Principal', 'Interest', 'Balance', 'Remaining to Repay'];
      if (calculatorType === 'missed') {
        headers.push('Missed', 'Late Fee');
      } else if (calculatorType === 'interest') {
        headers.push('Interest Frozen', 'Original Interest');
      }
      
      // Add header row
      scheduleSheet.addRow(headers);
      
      // Add data rows
      scheduleToExport.forEach(payment => {
        let row = [
          payment.month,
          payment.payment.toString(),
          payment.principal.toString(),
          payment.interest.toString(),
          payment.balance.toString(),
          payment.remainingToRepay ? payment.remainingToRepay.toString() : '0'
        ];
        
        if (calculatorType === 'missed') {
          row.push(payment.missed ? 'Yes' : 'No');
          row.push(payment.lateFee ? payment.lateFee.toString() : '0');
        } else if (calculatorType === 'interest') {
          row.push(payment.interestFrozen ? 'Yes' : 'No');
          row.push(payment.originalInterest ? payment.originalInterest.toString() : '');
        }
        
        scheduleSheet.addRow(row);
      });
    }
    
    // Generate Excel file buffer and download
    const fileName = `loan-schedule-${calculatorType}-${new Date().toISOString().split('T')[0]}.xlsx`;
    
    workbook.xlsx.writeBuffer().then(buffer => {
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      saveAs(blob, fileName);
    });
    
    console.log('Excel export completed successfully');
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    Sentry.captureException(error);
    alert('An error occurred while exporting to Excel. Please try again.');
  }
}

/**
 * Export amortization schedule as CSV
 * @param {Object} data - Calculation results and schedule
 * @param {string} calculatorType - Type of calculator used
 */
export function exportToCsv(data, calculatorType = 'standard') {
  try {
    // Get schedule for early repayment
    let scheduleToExport = data.schedule;
    if (calculatorType === 'early') {
      if (data.reducePaymentScenario && data.reducePaymentScenario.schedule) {
        scheduleToExport = data.reducePaymentScenario.schedule;
      } else if (data.reduceTermScenario && data.reduceTermScenario.schedule) {
        scheduleToExport = data.reduceTermScenario.schedule;
      } else {
        scheduleToExport = [];
      }
    }

    if (!scheduleToExport || scheduleToExport.length === 0) {
      alert('No schedule data available to export');
      return;
    }
    
    // Define headers based on calculator type
    let headers = ['Month', 'Payment', 'Principal', 'Interest', 'Balance', 'Remaining to Repay'];
    if (calculatorType === 'missed') {
      headers.push('Missed', 'Late Fee');
    } else if (calculatorType === 'interest') {
      headers.push('Interest Frozen', 'Original Interest');
    }
    
    // Start with headers
    let csvContent = headers.join(',') + '\r\n';
    
    // Add data rows
    scheduleToExport.forEach(payment => {
      let row = [
        payment.month,
        payment.payment.toString(),
        payment.principal.toString(),
        payment.interest.toString(),
        payment.balance.toString(),
        payment.remainingToRepay ? payment.remainingToRepay.toString() : '0'
      ];
      
      if (calculatorType === 'missed') {
        row.push(payment.missed ? 'Yes' : 'No');
        row.push(payment.lateFee ? payment.lateFee.toString() : '0');
      } else if (calculatorType === 'interest') {
        row.push(payment.interestFrozen ? 'Yes' : 'No');
        row.push(payment.originalInterest ? payment.originalInterest.toString() : '');
      }
      
      csvContent += row.join(',') + '\r\n';
    });
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `loan-schedule-${calculatorType}-${new Date().toISOString().split('T')[0]}.csv`;
    saveAs(blob, fileName);
    
    console.log('CSV export completed successfully');
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    Sentry.captureException(error);
    alert('An error occurred while exporting to CSV. Please try again.');
  }
}

/**
 * Export calculation results based on selected format
 * @param {Object} data - Calculation results and schedule
 * @param {string} format - Export format (pdf, excel, csv)
 * @param {string} calculatorType - Type of calculator used
 */
export function exportData(data, format, calculatorType) {
  switch (format.toLowerCase()) {
    case 'pdf':
      exportToPdf(data, calculatorType);
      break;
    case 'excel':
      exportToExcel(data, calculatorType);
      break;
    case 'csv':
      exportToCsv(data, calculatorType);
      break;
    default:
      console.error('Unsupported export format:', format);
      alert('Unsupported export format. Please choose PDF, Excel, or CSV.');
  }
}